import { FormEvent, useState } from "react";
import { ArrowLeft, Bot, CircleAlert, Loader2, Send, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { AssistantMessage, requestSymptomAssistance, SymptomAssistantResponse } from "@/services/aiAssistant";

type DisplayMessage = AssistantMessage & { response?: SymptomAssistantResponse };

const urgencyStyles = {
  emergency: "border-[#efb1b1] bg-[#fff3f3] text-[#a53f3f]",
  same_day: "border-[#f2d49b] bg-[#fff9e9] text-[#94651d]",
  routine: "border-[#cfe3d8] bg-[#f1faf4] text-[#237e76]",
  unknown: "border-[#dbe9e4] bg-[#f7fbf9] text-[#566966]",
} as const;

const SymptomAssistantPage = () => {
  const { language } = useLanguage();
  const bn = language === "bn";
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [lastResponse, setLastResponse] = useState<SymptomAssistantResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || loading) return;

    const nextMessages: AssistantMessage[] = [...messages, { role: "user", content }];
    setMessages((previous) => [...previous, { role: "user", content }]);
    setInput("");
    setError("");
    setLoading(true);

    try {
      const response = await requestSymptomAssistance(nextMessages);
      setLastResponse(response);
      setMessages((previous) => [...previous, { role: "assistant", content: response.message, response }]);
    } catch (requestError: any) {
      setError(requestError?.message || (bn ? "সহায়কটি এখন উপলব্ধ নয়।" : "The assistant is temporarily unavailable."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-28 text-[#203f3c]">
      <div className="mx-auto max-w-md px-4 pb-8 pt-5 sm:px-5 sm:pt-7">
        <header className="relative flex items-start justify-center">
          <Link to="/patient-home" aria-label={bn ? "ফিরে যান" : "Go back"} className="absolute left-0 rounded-full p-1.5 text-[#174e49]">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#e4f3ef] text-[#237e76]">
              <Bot className="h-6 w-6" />
            </div>
            <h1 className="mt-3 font-serif text-[25px] font-extrabold text-[#174e49]">{bn ? "স্বাস্থ্য সহায়ক" : "Health assistant"}</h1>
            <p className="mt-1 text-xs text-[#6d7b78]">{bn ? "উপসর্গ বুঝতে একটি নিরাপদ প্রথম ধাপ।" : "A safe first step for understanding symptoms."}</p>
          </div>
        </header>

        <section className="mt-6 rounded-[20px] border border-[#dbeae1] bg-[#f0f8f2] p-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#237e76]" />
            <p className="text-xs leading-5 text-[#52706f]">
              {bn
                ? "এটি রোগ নির্ণয় বা ওষুধের পরামর্শ দেয় না। জরুরি উপসর্গ হলে এখনই জরুরি সেবা নিন।"
                : "This assistant does not diagnose conditions or prescribe medicine. If symptoms may be urgent, seek emergency care now."}
            </p>
          </div>
        </section>

        <div className="mt-5 space-y-3">
          {messages.length === 0 && (
            <div className="rounded-[20px] border border-[#e2ebe4] bg-white p-5">
              <div className="flex items-center gap-2 text-[#237e76]">
                <Stethoscope className="h-5 w-5" />
                <h2 className="font-serif text-lg font-extrabold text-[#174e49]">{bn ? "আপনি কেমন অনুভব করছেন?" : "How are you feeling?"}</h2>
              </div>
              <p className="mt-2 text-xs leading-5 text-[#647470]">
                {bn ? "কখন শুরু হয়েছে, কোথায় হচ্ছে এবং কতটা তীব্র—যতটা স্বাচ্ছন্দ্যবোধ করেন লিখুন।" : "Share what you are experiencing, when it started, where it is, and how severe it feels."}
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div key={`${message.role}-${index}`} className={`rounded-[20px] border p-4 ${message.role === "user" ? "ml-8 border-[#d6e8df] bg-[#e4f3ef]" : "mr-4 border-[#e2ebe4] bg-white"}`}>
              <p className="whitespace-pre-wrap text-sm leading-6 text-[#405b57]">{message.content}</p>
              {message.response && (
                <div className="mt-4 space-y-3">
                  <div className={`rounded-xl border px-3 py-2 text-xs font-bold ${urgencyStyles[message.response.urgency]}`}>
                    {message.response.urgency === "emergency"
                      ? (bn ? "জরুরি সেবা নিন" : "Seek emergency care now")
                      : message.response.urgency === "same_day"
                        ? (bn ? "আজই একজন চিকিৎসকের পরামর্শ নিন" : "Consider same-day medical advice")
                        : message.response.urgency === "routine"
                          ? (bn ? "রুটিন পরামর্শ বিবেচনা করুন" : "Routine medical advice may be appropriate")
                          : (bn ? "তীব্রতা নিশ্চিত নয়" : "Urgency could not be determined")}
                  </div>
                  {message.response.redFlags.length > 0 && (
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-bold text-[#9b5252]"><CircleAlert className="h-3.5 w-3.5" /> {bn ? "সতর্কতার লক্ষণ" : "Red flags"}</p>
                      <ul className="mt-1 list-disc space-y-1 pl-5 text-xs leading-5 text-[#6d5959]">{message.response.redFlags.map((item) => <li key={item}>{item}</li>)}</ul>
                    </div>
                  )}
                  {message.response.departments.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-[#20534f]">{bn ? "যে বিভাগ বিবেচনা করতে পারেন" : "Department to consider"}</p>
                      <p className="mt-1 text-xs leading-5 text-[#647470]">{message.response.departments.join(" · ")}</p>
                    </div>
                  )}
                  {message.response.followUpQuestions.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-[#20534f]">{bn ? "আরও কিছু প্রশ্ন" : "A few follow-up questions"}</p>
                      <ul className="mt-1 list-disc space-y-1 pl-5 text-xs leading-5 text-[#647470]">{message.response.followUpQuestions.map((item) => <li key={item}>{item}</li>)}</ul>
                    </div>
                  )}
                  <p className="border-t border-[#edf1ed] pt-2 text-[10px] leading-4 text-[#7a8986]">{message.response.disclaimer}</p>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="mr-4 flex items-center gap-2 rounded-[20px] border border-[#e2ebe4] bg-white p-4 text-xs text-[#647470]">
              <Loader2 className="h-4 w-4 animate-spin text-[#237e76]" />
              {bn ? "উত্তর তৈরি হচ্ছে..." : "Reviewing your information..."}
            </div>
          )}
        </div>

        {lastResponse?.followUpQuestions.length ? (
          <div className="mt-4 rounded-[18px] border border-[#e2ebe4] bg-white p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#71807d]">{bn ? "উত্তর দিতে চাইলে লিখুন" : "You can answer in your next message"}</p>
            <p className="mt-2 text-xs leading-5 text-[#647470]">{lastResponse.followUpQuestions[0]}</p>
          </div>
        ) : null}

        {error && <p className="mt-3 rounded-xl border border-[#efcaca] bg-[#fff5f5] px-3 py-2 text-xs text-[#b35f5f]">{error}</p>}

        <form onSubmit={submit} className="sticky bottom-3 z-10 mt-4 flex items-end gap-2 rounded-2xl border border-[#dbe9e4] bg-white p-2 shadow-[0_8px_24px_rgba(32,63,60,0.08)]">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            rows={2}
            maxLength={2000}
            disabled={loading}
            placeholder={bn ? "আপনার উপসর্গ লিখুন..." : "Describe your symptoms..."}
            className="min-h-[48px] flex-1 resize-none bg-transparent px-2 py-2 text-sm outline-none placeholder:text-[#8a9693] disabled:opacity-60"
          />
          <button type="submit" disabled={!input.trim() || loading} aria-label={bn ? "পাঠান" : "Send"} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#258e7f] text-white disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SymptomAssistantPage;